/** Saleor media as the PDP route maps it, before gallery selection. */
export interface UgSourceImage {
  url: string;
  alt: string;
  /** `url(size: 0)` — the untouched source file, the only animated copy. */
  originalUrl?: string | null;
  /** Original filename; the only signal that an asset is animated. */
  sourcePath?: string | null;
  /** `metadata.delta` from Saleor. `"0"` marks the listing-card packshot. */
  delta?: string | null;
}

/** A gallery-ready frame: `url` already points at the right copy. */
export interface UgGalleryImage {
  url: string;
  alt: string;
  delta?: string | null;
}

/**
 * Formats that carry motion inside an `<img>`. Saleor types every one of these
 * as `IMAGE`, so the original filename is the only signal available.
 *
 * They break in a specific way: `NEXT_PUBLIC_IMAGES_FORMAT` is AVIF, and asking
 * Saleor for `format: AVIF` flattens an animated GIF to its first frame before
 * the CDN or the browser ever sees it — the asset looks like a stalled video.
 * Verified on UGCC001 media[2]: `url(size:1200, format: WEBP)` returns
 * image/webp, `url(size:1200)` returns image/gif.
 *
 * `.webp` is deliberately NOT here. Animated WebP exists, but WebP is also a
 * perfectly ordinary still format and a likely PIM upload choice — treating
 * every `.webp` as animated would route static assets through `url(size: 0)`,
 * i.e. serve the full-size original (18.7 MB for UGCC001's) to fix a problem
 * they do not have. GIF and APNG are only ever used when motion is wanted.
 */
const ANIMATED_EXTENSIONS = ['.gif', '.apng'];

/** `"UGCC001/02_gallery__couturecabriolet.gif"` → true. */
export function isAnimatedSource(sourcePath: string | null | undefined): boolean {
  if (!sourcePath) return false;
  const lower = sourcePath.toLowerCase();
  return ANIMATED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** The spec's marker for the listing-card packshot. */
const MAIN_IMAGE_DELTA = '0';

/**
 * Drop the card packshot from the gallery, keyed off `delta` per the spec.
 *
 * Three guards, each protecting against a way the PIM data can fall short —
 * because an empty or missing gallery is worse than one repeated image:
 *
 * - A single media item is kept whatever it is tagged: that is a PIM gap, not a
 *   card-plus-gallery set.
 * - Untagged media fall back to the positional rule. No UG product is untagged
 *   today (checked across all 37 in `ch-chf`), but Gallet and Breitling assets
 *   carry no `delta`, and this helper should not depend on who calls it.
 * - If every item is tagged `"0"`, all are kept rather than returning nothing.
 */
export function selectUgGalleryImages<T extends UgGalleryImage>(images: T[]): T[] {
  if (images.length <= 1) return images;

  const isTagged = images.some((image) => image.delta != null);
  if (!isTagged) return images.slice(1);

  const gallery = images.filter((image) => image.delta !== MAIN_IMAGE_DELTA);
  return gallery.length > 0 ? gallery : images;
}

/**
 * The card packshot — the one frame `selectUgGalleryImages` removes.
 *
 * The pre-order drawer wants exactly this image rather than the first gallery
 * frame: it is the clean cut-out the listing cards use, while the gallery copies
 * are studio shots on a ground. Live shows the packshot beside the form.
 *
 * Mirrors the selector's guards: an untagged set falls back to the positional
 * rule, and a single-item set is its own packshot.
 */
export function selectUgPackshot<T extends UgGalleryImage>(images: T[]): T | null {
  if (images.length === 0) return null;

  const tagged = images.find((image) => image.delta === MAIN_IMAGE_DELTA);
  if (tagged) return tagged;

  // Some media carry `delta` but none is the packshot — there is nothing to pick.
  if (images.some((image) => image.delta != null)) return null;

  return images[0];
}

/**
 * Point one image's `url` at the copy that should actually render.
 *
 * Animated sources swap to `originalUrl`, because every Saleor-resized copy is a
 * single frame. The result still goes through the CDN loader downstream —
 * Cloudflare preserves frames while resizing, so the browser is not handed the
 * full-size original.
 */
function resolveSourceUrl(image: UgSourceImage): string {
  const useOriginal = isAnimatedSource(image.sourcePath) && Boolean(image.originalUrl);
  return useOriginal && image.originalUrl ? image.originalUrl : image.url;
}

/**
 * Raw Saleor media → gallery-ready frames. Applies both rules in this file's
 * header: the animated-source URL swap, then the packshot filter.
 *
 * Preserves any extra fields the caller carries, and drops the PIM-only
 * plumbing (`originalUrl` / `sourcePath`) that no renderer needs.
 */
export function resolveUgGalleryImages<T extends UgSourceImage>(
  images: T[]
): Array<Omit<T, 'originalUrl' | 'sourcePath'>> {
  const resolved = images.map((image) => {
    const { originalUrl: _originalUrl, sourcePath: _sourcePath, ...rest } = image;
    return { ...rest, url: resolveSourceUrl(image), delta: image.delta ?? null };
  });

  return selectUgGalleryImages(resolved);
}

/**
 * Raw Saleor media → the card packshot, through the same URL resolution the
 * gallery uses. Returns null when the media carry no packshot.
 */
export function resolveUgPackshot<T extends UgSourceImage>(
  images: T[]
): Omit<T, 'originalUrl' | 'sourcePath'> | null {
  const resolved = images.map((image) => {
    const { originalUrl: _originalUrl, sourcePath: _sourcePath, ...rest } = image;
    return { ...rest, url: resolveSourceUrl(image), delta: image.delta ?? null };
  });

  return selectUgPackshot(resolved);
}
